<script lang="ts">
import { defineComponent, PropType, toRef, ref, watch } from 'vue'
import CorporateInfoStep from './CorporateInfo.vue'
import ScaleStep from './Scale.vue'
import ListingStatusStep from './ListingStatus.vue'
import PositionLevelStep from './PositionLevel.vue'
import ProfessionStep from './Profession.vue'
import ProfessionDomainStep from './ProfessionDomain.vue'
import IndustryStep from './Industry.vue'
import NewsLetterStep from './NewsLetter.vue'
import StepWizard from '@/components/step/StepWizard.vue'
import userCareer from '@/models/userCareer'
import { sendGtmEvent } from '@/utilities/googleTagManager'

export default defineComponent({
  name: 'ForWorkMaillAddress',

  components: {
    ScaleStep,
    CorporateInfoStep,
    ListingStatusStep,
    PositionLevelStep,
    ProfessionStep,
    ProfessionDomainStep,
    IndustryStep,
    NewsLetterStep,
    StepWizard,
  },

  props: {
    companyName: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateZipcode: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateAddress1: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateAddress2: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateNumber: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateEmployeeNum: {
      type: Object as PropType<InputNumber>,
      required: true,
    },

    departmentAndPosition: {
      type: Object as PropType<InputText>,
      required: true,
    },

    phoneNumber: {
      type: Object as PropType<InputText>,
      required: true,
    },

    scale: {
      type: Object as PropType<InputText>,
      required: true,
    },

    listingStatus: {
      type: Object as PropType<InputText>,
      required: true,
    },

    positionLevel: {
      type: Object as PropType<InputText>,
      required: true,
    },

    profession: {
      type: Object as PropType<InputText>,
      required: true,
    },

    professionDomain: {
      type: Object as PropType<InputMultiSelect>,
      required: true,
    },

    industry: {
      type: Object as PropType<InputText>,
      required: true,
    },

    optionalNewsletterSettings: {
      type: Object as PropType<OptionalNewsletterSettings>,
      required: true,
    },
  },

  emits: [
    'update:companyName',
    'select:companyLeadInfo',
    'update:departmentAndPosition',
    'update:phoneNumber',
    'update:scale',
    'update:listingStatus',
    'update:positionLevel',
    'update:profession',
    'update:professionDomain',
    'update:industry',
    'return:entrance',
    'transition:step',
    'resolved:maxStep',
    'completed',
  ],

  setup(props, { emit }) {
    // constants
    const GA_EVENT_NAME = 'sign_up_next' as const

    const { isShowProfessionDomain } = userCareer(toRef({ profession: props.profession }))
    const stepManagerEl = ref()

    watch(() => stepManagerEl.value?.currentStep, (newValue) => {
      if (newValue) {
        emit('transition:step', newValue)
      }
    })

    watch(() => stepManagerEl.value?.maxStep, (newValue) => {
      if (newValue) {
        emit('resolved:maxStep', newValue)
      }
    })

    const next = (gaEventParamValue?: string) => {
      if (stepManagerEl.value) {
        if (gaEventParamValue) sendGtmEvent(GA_EVENT_NAME, gaEventParamValue)
        stepManagerEl.value.next()
      }
    }

    const prev = () => {
      if (stepManagerEl.value) stepManagerEl.value.prev()
    }

    const onCompleted = () => {
      sendGtmEvent(GA_EVENT_NAME, 'work_email_newsletter')
      emit('completed')
    }

    return {
      // data
      stepManagerEl,

      // computed
      isShowProfessionDomain,

      // methods
      next,
      prev,
      onCompleted,
    }
  },
})
</script>

<template>
  <StepWizard ref="stepManagerEl">
    <CorporateInfoStep
      namespace="userRegistrationStep-corporateInfo"
      :company-name="companyName"
      :corporate-zipcode="corporateZipcode"
      :corporate-address1="corporateAddress1"
      :corporate-address2="corporateAddress2"
      :corporate-number="corporateNumber"
      :corporate-employee-num="corporateEmployeeNum"
      :department-and-position="departmentAndPosition"
      :phone-number="phoneNumber"
      key="corporateInfo"
      @update:company-name="$emit('update:companyName', $event)"
      @select:company-lead-info="$emit('select:companyLeadInfo', $event)"
      @update:department-and-position="$emit('update:departmentAndPosition', $event)"
      @update:phone-number="$emit('update:phoneNumber', $event)"
      @click:next="next('work_email_corporate_info')"
      @click:prev="$emit('return:entrance')">
    </CorporateInfoStep>
    <ScaleStep
      namespace="userRegistrationStep-scale"
      :scale="scale"
      key="scale"
      @update:scale="$emit('update:scale', $event)"
      @click:next="next('work_email_scale')"
      @click:prev="prev" />
    <ListingStatusStep
      namespace="userRegistrationStep-listingStatus"
      :listing-status="listingStatus"
      key="listingStatus"
      @update:listing-status="$emit('update:listingStatus', $event)"
      @click:next="next('work_email_listing_status')"
      @click:prev="prev" />
    <PositionLevelStep
      namespace="userRegistrationStep-positionLevel"
      :position-level="positionLevel"
      key="positionLevel"
      @update:position-level="$emit('update:positionLevel', $event)"
      @click:next="next('work_email_position_level')"
      @click:prev="prev" />
    <ProfessionStep
      namespace="userRegistrationStep-profession"
      :profession="profession"
      key="profession"
      @update:profession="$emit('update:profession', $event)"
      @click:next="next('work_email_profession')"
      @click:prev="prev" />
    <ProfessionDomainStep
      v-if="isShowProfessionDomain"
      namespace="userRegistrationStep-professionDomain"
      :profession="profession"
      :profession-domain="professionDomain"
      key="professionDomain"
      @update:profession-domain="$emit('update:professionDomain', $event)"
      @click:next="next('work_email_profession_domain')"
      @click:prev="prev" />
    <IndustryStep
      namespace="userRegistrationStep-industry"
      :industry="industry"
      key="industry"
      @update:industry="$emit('update:industry', $event)"
      @click:next="next('work_email_industry')"
      @click:prev="prev" />
    <NewsLetterStep
      namespace="userRegistrationStep-newsletter"
      :input-optional-newsletter-info="optionalNewsletterSettings"
      key="newsletter"
      @click:next="onCompleted"
      @click:prev="prev" />
  </StepWizard>
</template>
